import { useState } from 'react';
import * as React from 'react';
import {
    FacebookColourSmall,
    WhatsappColourSmall,
    TwitterColourSmall
} from '@blueprint/icons';
import {
    trackClipboardShare,
    trackFacebookShare,
    trackTwitterShare,
    trackWhatsAppShare
} from './ListingSharing.tracking';
import {
    ShareLinkButton,
    ShareButton,
    ShareList,
    ShareListItem,
    ShareListItemMobileOnly,
    ShareHeading,
    Arrow,
    LinkIcon
} from './ListingSharing.styles';
import {
    createFacebookLink,
    createTwitterLink,
    createWhatsappLink,
    createTrackedPropertyTwitterLink
} from '@zoopla/shared/social';

export type SharingCategory = 'User Account' | 'listing details';
export type SharingType = 'saved' | 'tracked';

const ZOOPLA_PRODUCTION_URL = 'https://www.zoopla.co.uk';

export type ListingSharingProps = {
    listingId: string;
    title: string;
    listingUrl: string;
    sharingCategory?: SharingCategory;
    sharingType?: SharingType;
};

const ListingSharing = ({
    listingId,
    title,
    listingUrl,
    sharingCategory,
    sharingType = 'saved'
}: ListingSharingProps) => {
    const url = `${ZOOPLA_PRODUCTION_URL}${listingUrl}`;

    const [copyText, setCopyText] = useState('Copy link');

    const facebookLink = createFacebookLink(url);
    const whatsappLink = createWhatsappLink(url);

    const twitterSavedPropertyLink = createTwitterLink(url, title);
    const twitterTrackedPropertyLink = createTrackedPropertyTwitterLink(
        url,
        title
    );

    const twitterLink =
        sharingType === 'tracked'
            ? twitterTrackedPropertyLink
            : twitterSavedPropertyLink;

    return (
        <>
            <ShareHeading size="5">
                Share this property with your friends and family
            </ShareHeading>
            <ShareList>
                <ShareListItem>
                    <ShareLinkButton
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            trackFacebookShare(
                                parseInt(listingId),
                                sharingCategory
                            );
                        }}
                        target="_blank"
                        href={facebookLink}
                        data-testid="listing-sharing-facebook"
                    >
                        <FacebookColourSmall /> <span>Facebook</span> <Arrow />
                    </ShareLinkButton>
                </ShareListItem>
                <ShareListItem>
                    <ShareLinkButton
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            trackTwitterShare(
                                parseInt(listingId),
                                sharingCategory
                            );
                        }}
                        target="_blank"
                        href={twitterLink}
                        data-testid="listing-sharing-twitter"
                    >
                        <TwitterColourSmall /> <span>Twitter</span> <Arrow />
                    </ShareLinkButton>
                </ShareListItem>
                <ShareListItemMobileOnly>
                    <ShareLinkButton
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            trackWhatsAppShare(
                                parseInt(listingId),
                                sharingCategory
                            );
                        }}
                        href={whatsappLink}
                        data-action="share/whatsapp/share"
                        data-testid="listing-sharing-whatsapp"
                    >
                        <WhatsappColourSmall /> <span>WhatsApp</span> <Arrow />
                    </ShareLinkButton>
                </ShareListItemMobileOnly>
                {
                    // It is known that navigator.clipboard() is unsupported in older versions of Safari,
                    // hence checking for 'clipboard' in navigator before invoking
                }
                {'clipboard' in navigator && ( //eslint-disable-line compat/compat
                    <ShareListItem>
                        <ShareButton
                            onClick={() => {
                                trackClipboardShare(
                                    parseInt(listingId),
                                    sharingCategory
                                );
                                navigator.clipboard //eslint-disable-line compat/compat
                                    .writeText(url)
                                    .then(() => setCopyText('Link copied!'))
                                    .catch(() => setCopyText('Copy error'));
                            }}
                            data-testid="listing-sharing-copylink"
                        >
                            <LinkIcon /> <span>{copyText}</span>{' '}
                        </ShareButton>
                    </ShareListItem>
                )}
            </ShareList>
        </>
    );
};

export default ListingSharing;
